<section class="reports-page" aria-labelledby="reports-page-title">
  <header class="reports-header">
    <h1 id="reports-page-title" class="reports-title">Reports</h1>
    <p class="reports-subtitle">
      Generate, view, export, and schedule reports for your car wash business.
    </p>
  </header>

  <div class="reports-segment" role="tablist" aria-label="Reports sections">
    @for (tab of tabs; track tab.id) {
      <button
        type="button"
        role="tab"
        class="reports-segment__tab"
        [class.reports-segment__tab--active]="activeTab === tab.id"
        [attr.aria-selected]="activeTab === tab.id"
        [id]="'reports-tab-' + tab.id"
        [attr.aria-controls]="'reports-panel-' + tab.id"
        (click)="setActiveTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    }
  </div>

  @if (activeTab === 'view') {
    <div
      class="reports-panel"
      role="tabpanel"
      id="reports-panel-view"
      aria-labelledby="reports-tab-view"
    >
      <section class="reports-card reports-generate" aria-labelledby="generate-report-title">
        <div class="reports-card__header">
          <h2 id="generate-report-title" class="reports-card__title">Generate Report</h2>
          <p class="reports-card__description">
            Choose a report type and filters, then click Filter to preview it below.
          </p>
        </div>

        <form class="reports-filters" (ngSubmit)="onFilter()">
          <div class="reports-field">
            <label class="reports-field__label" for="report-type">Report type</label>
            <mat-select
              #reportTypeSelect
              id="report-type"
              class="form-select reports-type-dropdown"
              [(ngModel)]="reportType"
              name="reportType"
              placeholder="Select report type"
              (openedChange)="onPrimaryFilterOpened('reportType', $event)"
              (selectionChange)="onReportTypeChange()"
            >
              <mat-option *ngFor="let reportType of reportTypeOptions" [value]="reportType.value">{{
                reportType.label
              }}</mat-option>
            </mat-select>
          </div>

          <div class="reports-field">
            <label class="reports-field__label" id="report-location-label">Location</label>
            <div class="reports-multi">
              <app-checkbox-multi-dropdown
                #locationDropdown
                appearance="input"
                ariaLabel="Location"
                ariaLabelledBy="report-location-label"
                [options]="locationOptions"
                [selectedIds]="selectedLocationIds"
                allOptionLabel="All Locations"
                [multipleSelectionText]="'{{count}} Locations'"
                (openedChange)="onPrimaryFilterOpened('location', $event)"
                (selectedIdsChange)="onLocationIdsChange($event)"
              ></app-checkbox-multi-dropdown>
            </div>
          </div>

          @if (usesEmployeeFilter) {
            <div class="reports-field">
              <label class="reports-field__label" id="report-employees-label">Employees</label>
              <div class="reports-multi">
                <app-checkbox-multi-dropdown
                  #employeesDropdown
                  appearance="input"
                  ariaLabel="Employees"
                  ariaLabelledBy="report-employees-label"
                  [options]="employeeOptions"
                  [selectedIds]="selectedEmployeeIds"
                  [disabled]="!employeesLoading && employeeOptions.length === 0"
                  [label]="
                    employeesLoading
                      ? 'Loading employees…'
                      : employeeOptions.length === 0
                        ? 'No employees'
                        : 'All Employees'
                  "
                  allOptionLabel="All Employees"
                  [multipleSelectionText]="'{{count}} Employees'"
                  (openedChange)="onPrimaryFilterOpened('employees', $event)"
                  (selectedIdsChange)="onEmployeeIdsChange($event)"
                ></app-checkbox-multi-dropdown>
                @if (!employeesLoading && employeeOptions.length === 0) {
                  <p class="reports-field__hint" role="status">
                    No employees found for the selected location.
                  </p>
                }
              </div>
            </div>
          }

          @if (reportType === 'general-sales') {
            <div class="reports-field">
              <label class="reports-field__label" for="report-period">Reporting period</label>
              <mat-select
                #datePresetSelect
                id="report-period"
                class="form-select reports-type-dropdown"
                [(ngModel)]="selectedDatePreset"
                name="datePreset"
                placeholder="Select period"
                (openedChange)="onPrimaryFilterOpened('datePreset', $event)"
                (selectionChange)="onDatePresetChange($event.value)"
              >
                <mat-option *ngFor="let preset of datePresetOptions" [value]="preset.id">{{
                  preset.label
                }}</mat-option>
              </mat-select>
            </div>
          }

          @if (usesDateRange && showCustomDateRange) {
            <div class="reports-field reports-field--date">
              <label class="reports-field__label" id="report-start-date-label">Start date</label>
              <div class="reports-date">
                <app-wi-date-range-picker
                  #reportStartDatePicker
                  mode="date"
                  ariaLabel="Start date"
                  ariaLabelledBy="report-start-date-label"
                  [selectedDate]="selectedStartDate"
                  [showActions]="false"
                  [disableDate]="disableFutureDates"
                  placeholder="Select start date"
                  (openedChange)="onPrimaryFilterOpened('startDate', $event)"
                  (dateChange)="onStartDateChange($event)"
                ></app-wi-date-range-picker>
              </div>
            </div>

            <div class="reports-field reports-field--date">
              <label class="reports-field__label" id="report-end-date-label">End date</label>
              <div class="reports-date">
                <app-wi-date-range-picker
                  #reportEndDatePicker
                  mode="date"
                  ariaLabel="End date"
                  ariaLabelledBy="report-end-date-label"
                  [selectedDate]="selectedEndDate"
                  [showActions]="false"
                  [disableDate]="disableFutureDates"
                  placeholder="Select end date"
                  (openedChange)="onPrimaryFilterOpened('endDate', $event)"
                  (dateChange)="onEndDateChange($event)"
                ></app-wi-date-range-picker>
              </div>
            </div>
          } @else if (!usesDateRange) {
            <!-- Single-date control for the Shift report only. The General Sales
                 Report drives its window from the Reporting period selector, so it
                 must not fall through to this branch. -->
            <div class="reports-field reports-field--date">
              <label class="reports-field__label" id="report-date-label">Date</label>
              <div class="reports-date">
                <app-wi-date-range-picker
                  #reportDatePicker
                  mode="date"
                  ariaLabel="Date"
                  ariaLabelledBy="report-date-label"
                  [selectedDate]="selectedDate"
                  [showActions]="false"
                  [disableDate]="disableFutureDates"
                  placeholder="Select date"
                  (openedChange)="onPrimaryFilterOpened('date', $event)"
                  (dateChange)="onDateChange($event)"
                ></app-wi-date-range-picker>
              </div>
            </div>
          }

          @if (reportType === 'general-sales') {
            <div class="reports-field reports-field--filters">
              <span class="reports-field__label" aria-hidden="true">&nbsp;</span>
              <button
                type="button"
                class="reports-filter-toggle"
                [class.reports-filter-toggle--active]="activeFilterCount > 0"
                [attr.aria-expanded]="filtersPanelOpen"
                aria-controls="report-filters-panel"
                (click)="toggleFiltersPanel()"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M2 3.75h12M4.25 8h7.5M6.5 12.25h3"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                  />
                </svg>
                <span>Filters</span>
                @if (activeFilterCount > 0) {
                  <span class="reports-filter-toggle__badge">{{ activeFilterCount }}</span>
                }
                <svg
                  class="reports-filter-toggle__chevron"
                  [class.reports-filter-toggle__chevron--open]="filtersPanelOpen"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 5.5L7 8.5l3-3"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          }

          <div class="reports-actions">
            <button
              type="submit"
              class="reports-btn reports-btn--primary"
              [disabled]="
                reportLoading ||
                (usesEmployeeFilter && employeesLoading && employeeOptions.length === 0)
              "
            >
              {{
                reportLoading
                  ? usesDateRange
                    ? 'Generating…'
                    : 'Filtering…'
                  : usesDateRange
                    ? 'Generate report'
                    : 'Filter'
              }}
            </button>
            <button type="button" class="reports-btn reports-btn--clear" (click)="onClear()">
              Clear
            </button>
          </div>
        </form>

        @if (reportType === 'general-sales') {
          @if (activeFilterChips.length > 0) {
            <div class="reports-chips" role="list" aria-label="Active filters">
              @for (chip of activeFilterChips; track chip.key + chip.id) {
                <span class="reports-chip" role="listitem">
                  <span class="reports-chip__text">{{ chip.label }}</span>
                  @if (chip.sectionScoped) {
                    <span class="reports-chip__scope" title="Narrows the rewash tables only"
                      >rewash only</span
                    >
                  }
                  <button
                    type="button"
                    class="reports-chip__remove"
                    [attr.aria-label]="'Remove filter ' + chip.label"
                    (click)="removeFilterChip(chip)"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path
                        d="M3.5 3.5l5 5M8.5 3.5l-5 5"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                </span>
              }
              <button
                type="button"
                class="reports-chips__clear"
                (click)="onClearGeneralSalesFilters(); compareToPreviousPeriod = false"
              >
                Clear all
              </button>
            </div>
          }

          @if (filtersPanelOpen) {
            <div class="reports-filter-panel" id="report-filters-panel">
              <div class="reports-filter-panel__group">
                <span class="reports-filter-panel__legend">Sales filters</span>
                <div class="reports-filter-panel__grid">
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-customer-type-label"
                      >Customer type</label
                    >
                    <mat-select
                      class="form-select reports-type-dropdown"
                      [ngModel]="generalSalesFilters.customer_type"
                      name="customerType"
                      aria-labelledby="report-customer-type-label"
                      (ngModelChange)="onGeneralSalesFilterChange('customer_type', $event)"
                    >
                      <mat-option
                        *ngFor="let option of filterOptions?.customerTypes || []"
                        [value]="option.id"
                        >{{ option.name }}</mat-option
                      >
                    </mat-select>
                  </div>
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-package-label"
                      >Wash package</label
                    >
                    <div class="reports-multi">
                      <app-checkbox-multi-dropdown
                        appearance="input"
                        ariaLabel="Wash package"
                        ariaLabelledBy="report-package-label"
                        [options]="filterOptions?.packages || []"
                        [selectedIds]="generalSalesFilters.package_ids"
                        [disabled]="!filterOptionsLoading && !filterOptions?.packages?.length"
                        allOptionLabel="All packages"
                        [multipleSelectionText]="'{{count}} Packages'"
                        (selectedIdsChange)="onGeneralSalesFilterChange('package_ids', $event)"
                      ></app-checkbox-multi-dropdown>
                    </div>
                  </div>
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-payment-label"
                      >Payment type</label
                    >
                    <div class="reports-multi">
                      <app-checkbox-multi-dropdown
                        appearance="input"
                        ariaLabel="Payment type"
                        ariaLabelledBy="report-payment-label"
                        [options]="filterOptions?.paymentMethods || []"
                        [selectedIds]="generalSalesFilters.payment_methods"
                        [disabled]="!filterOptionsLoading && !filterOptions?.paymentMethods?.length"
                        allOptionLabel="All payment types"
                        [multipleSelectionText]="'{{count}} Payment types'"
                        (selectedIdsChange)="onGeneralSalesFilterChange('payment_methods', $event)"
                      ></app-checkbox-multi-dropdown>
                    </div>
                  </div>
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-discount-label"
                      >Discount / coupon type</label
                    >
                    <div class="reports-multi">
                      <app-checkbox-multi-dropdown
                        appearance="input"
                        ariaLabel="Discount or coupon type"
                        ariaLabelledBy="report-discount-label"
                        [options]="filterOptions?.discountTypes || []"
                        [selectedIds]="generalSalesFilters.discount_types"
                        [disabled]="!filterOptionsLoading && !filterOptions?.discountTypes?.length"
                        allOptionLabel="All discount types"
                        [multipleSelectionText]="'{{count}} Discount types'"
                        (selectedIdsChange)="onGeneralSalesFilterChange('discount_types', $event)"
                      ></app-checkbox-multi-dropdown>
                    </div>
                  </div>
                </div>
              </div>

              <div class="reports-filter-panel__group">
                <span class="reports-filter-panel__legend">
                  Rewash filters
                  <span class="reports-filter-panel__note">
                    narrow the rewash tables only &mdash; a wash is not attributed to an employee,
                    so these do not change sales totals
                  </span>
                </span>
                <div class="reports-filter-panel__grid">
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-rewash-reason-label"
                      >Rewash reason</label
                    >
                    <div class="reports-multi">
                      <app-checkbox-multi-dropdown
                        appearance="input"
                        ariaLabel="Rewash reason"
                        ariaLabelledBy="report-rewash-reason-label"
                        [options]="filterOptions?.rewashReasons || []"
                        [selectedIds]="generalSalesFilters.rewash_reasons"
                        [disabled]="!filterOptionsLoading && !filterOptions?.rewashReasons?.length"
                        [label]="
                          filterOptions?.rewashReasons?.length
                            ? 'All rewash reasons'
                            : 'No rewashes'
                        "
                        allOptionLabel="All rewash reasons"
                        [multipleSelectionText]="'{{count}} Reasons'"
                        (selectedIdsChange)="onGeneralSalesFilterChange('rewash_reasons', $event)"
                      ></app-checkbox-multi-dropdown>
                    </div>
                  </div>
                  <div class="reports-field">
                    <label class="reports-field__label" id="report-rewash-employee-label"
                      >Employee</label
                    >
                    <div class="reports-multi">
                      <app-checkbox-multi-dropdown
                        appearance="input"
                        ariaLabel="Employee"
                        ariaLabelledBy="report-rewash-employee-label"
                        [options]="filterOptions?.rewashEmployees || []"
                        [selectedIds]="generalSalesFilters.employee_ids"
                        [disabled]="
                          !filterOptionsLoading && !filterOptions?.rewashEmployees?.length
                        "
                        [label]="
                          filterOptions?.rewashEmployees?.length
                            ? 'All employees'
                            : 'No attributed rewashes'
                        "
                        allOptionLabel="All employees"
                        [multipleSelectionText]="'{{count}} Employees'"
                        (selectedIdsChange)="onGeneralSalesFilterChange('employee_ids', $event)"
                      ></app-checkbox-multi-dropdown>
                    </div>
                  </div>
                </div>
              </div>

              <div class="reports-filter-panel__group">
                <span class="reports-filter-panel__legend">Report options</span>
                <label class="reports-compare" for="report-compare">
                  <input
                    id="report-compare"
                    type="checkbox"
                    class="reports-compare__input"
                    [checked]="compareToPreviousPeriod"
                    (change)="onCompareChange($any($event.target).checked)"
                  />
                  <span class="reports-compare__text">Compare with previous period</span>
                </label>
              </div>
            </div>
          }
        }
      </section>

      <section class="reports-card reports-preview" aria-live="polite">
        @if (reportLoading) {
          <div class="reports-empty">
            <h3 class="reports-empty__title">
              @switch (reportType) {
                @case ('cash') {
                  Loading cash report…
                }
                @case ('general-sales') {
                  Loading general sales report…
                }
                @default {
                  Loading shift report…
                }
              }
            </h3>
            <p class="reports-empty__description">
              @switch (reportType) {
                @case ('cash') {
                  Fetching cash reconciliation data for the selected range.
                }
                @case ('general-sales') {
                  Aggregating sales, package mix, membership and leakage for the selected period.
                }
                @default {
                  Fetching close-out data for the selected filters.
                }
              }
            </p>
          </div>
        } @else if (reportError) {
          <div class="reports-empty">
            <h3 class="reports-empty__title">Could not load report</h3>
            <p class="reports-empty__description">{{ reportError }}</p>
            @if (reportType === 'general-sales') {
              <button
                type="button"
                class="reports-btn reports-btn--primary"
                style="margin-top: 12px"
                (click)="retryGeneralSalesReport()"
              >
                Retry
              </button>
            }
          </div>
        } @else if (reportType === 'shift' && shiftReport) {
          <app-shift-report-results
            [report]="shiftReport"
            [date]="shiftExportDate"
            [locationIds]="shiftExportLocationIds"
            [employeeIds]="shiftExportEmployeeIds"
          ></app-shift-report-results>
        } @else if (generalSalesHasNoData) {
          <div class="reports-empty">
            <h3 class="reports-empty__title">No sales data available for this period.</h3>
            <p class="reports-empty__description">
              Try selecting a different date range or location.
            </p>
          </div>
        } @else if (reportType === 'general-sales' && generalSalesReport) {
          @if (generalSalesPartialData) {
            <p class="reports-partial" role="status">
              Some metrics are unavailable for this period because supporting data is incomplete.
            </p>
          }
          <app-general-sales-report-results
            [report]="generalSalesReport"
            [exportDisabled]="!canExportGeneralSales"
            [exportingCsv]="exportingCsv"
            [exportingXlsx]="exportingXlsx"
            [exportingPdf]="exportingPdf"
            [includeCancelledOrdersInExport]="includeCancelledOrdersInExport"
            (includeCancelledOrdersInExportChange)="onIncludeCancelledOrdersInExportChange($event)"
            (exportCsv)="onExportGeneralSales('csv')"
            (exportXlsx)="onExportGeneralSales('xlsx')"
            (exportPdf)="onExportGeneralSales('pdf')"
          ></app-general-sales-report-results>
        } @else if (reportType === 'cash' && cashReport) {
          <app-cash-report-results
            [report]="cashReport"
            [startDate]="cashExportStartDate"
            [endDate]="cashExportEndDate"
            [employeeIds]="cashExportEmployeeIds"
          ></app-cash-report-results>
        } @else {
          <div class="reports-empty">
            <div class="reports-empty__icon" aria-hidden="true">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.333 5V11.6667C23.333 12.1087 23.5086 12.5326 23.8212 12.8452C24.1338 13.1577 24.5577 13.3333 24.9997 13.3333H31.6663M23.333 5H11.6663C10.7823 5 9.93443 5.35119 9.30931 5.97631C8.68419 6.60143 8.33301 7.44928 8.33301 8.33333V31.6667C8.33301 32.5507 8.68419 33.3986 9.30931 34.0237C9.93443 34.6488 10.7823 35 11.6663 35H28.333C29.2171 35 30.0649 34.6488 30.69 34.0237C31.3152 33.3986 31.6663 32.5507 31.6663 31.6667V13.3333M23.333 5L31.6663 13.3333M15 20H25M15 26.6667H25M15 13.3333H16.6667"
                  stroke="#555963"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="reports-empty__title">Choose filters to generate report</h3>
            <p class="reports-empty__description">
              @if (reportType === 'general-sales') {
                Pick a location (or all) and a reporting period<br />
                above. Then choose
                <strong>Generate report.</strong>
              } @else if (reportType === 'cash') {
                Pick a location, employee (or all) and date range<br />
                (max 31 days). Then choose
                <strong>Filter.</strong>
              } @else {
                Pick a location, an employee (or all) and date<br />
                above. Then choose
                <strong>Filter.</strong>
              }
            </p>
          </div>
        }
      </section>
    </div>
  }

  @if (activeTab === 'scheduled') {
    <div
      class="reports-panel"
      role="tabpanel"
      id="reports-panel-scheduled"
      aria-labelledby="reports-tab-scheduled"
    >
      <section class="reports-card reports-scheduled" aria-live="polite">
        <app-scheduled-reports-list></app-scheduled-reports-list>
      </section>
    </div>
  }
</section>
